import type { InstitutionName } from '@/components/design-system/avatar/bank-logo-repository';
import type { Client, File, User } from '@/components/Views/Company/File/file.types';
import type { UpdateClientForFileRequest } from '@/interfaces/file-client/file-client.types';
import type { Contact, ContactType, FileContactRequest } from '@/interfaces/file-contact/file-contact.types';
import type { PaginatedResponse } from '@/interfaces/pagination.types';
import type { Role } from '@/types/closinglock.types';

export interface Lender {
    id: number; // comes from 24/7
    name: InstitutionName;
    lender_address: string | null;
    logo_url: string | null;
    turnaround_time: number; // Time it takes lender to return payoff statements in hours. Guaranteed to be at least 72 hours.
    requires_seller_authorization: boolean; // Determines if the lender requires a seller authorization document
}

export interface LenderSearchRequest {
    query: string;
    page?: number;
    limit?: number;
}

export type LenderResponse = PaginatedResponse<Lender>;

export enum PayoffOrderStatus {
    REQUESTED = 'Requested',
    COMPLETED = 'Completed',
    UNRETRIEVABLE = 'Unretrievable',
    NEEDS_REVIEW = 'Needs Review',
}

export enum PayoffOrderVerification {
    VERIFIED = 'verified',
    UNVERIFIED = 'unverified',
}

export enum PayoffOrderInsurance {
    INSURED = 'insured',
    NOT_INSURED = 'not_insured',
}

export interface RetrievePayoffOrderLetter {
    id: string;
    document_url: string;
}

export interface PayoffOrder {
    id: number;
    borrower_name: string;
    lender_name: string;
    date_requested: string; // ISO 8601 timestamp (created_at)
    requested_by: string; // User's full name
    status: PayoffOrderStatus;
    verification: PayoffOrderVerification | null;
    insurance: PayoffOrderInsurance | null;
    completed_date: string; // ISO 8601 timestamp
    role?: Role;
    last_retrieved_letter: RetrievePayoffOrderLetter | null;
}

export type PayoffOrdersResponse = PaginatedResponse<PayoffOrder>;

export enum PayoffOrderDocumentType {
    CONTRACT = 'contract',
    PAYOFF_STATEMENT = 'payoff_statement',
    VERIFICATION_RECEIPT = 'verification_receipt',
    SELLER_AUTHORIZATION = 'seller_authorization',
    UNKNOWN = 'unknown',
}

export interface PayoffOrderDocument {
    document_title: string;
    document_closinglock_url: string;
    uploaded_by: string;
    uploaded_date: string; // ISO 8601 timestamp
    completed_date: string; // ISO 8601 timestamp
    type: PayoffOrderDocumentType;
}

/**
 * null = Unknown / not yet verified (no verification attempted)
 * false = Verification attempted and failed (unverified)
 * true = Verification succeeded (verified)
 */
export type PayoffVerifiedStatus = boolean | null;

/**
 * null = Unknown / not yet verified (no verification attempted)
 * false = Verification attempted and failed (not insured)
 * true = Verification succeeded (insured)
 */
export type PayoffInsuredStatus = boolean | null;

export interface PayoffDetailsResponse {
    data: PayoffDetailsPane;
}

export interface PayoffDetailsPane {
    id: number;
    status: PayoffOrderStatus;
    has_message: boolean; // controls display of alert banner
    message_type: 'error' | 'warning' | null; // null = no message available
    message: string;
    borrower_name: string;
    property_address: string;
    loan_information: {
        payoff_amount: number | null; // value is in cents; null = unknown / not yet available
        good_through_date: string; // ISO 8601 date
        per_diem: number | null; // value is in cents; null = unknown / not yet available
        disburse_funds_button: boolean; // always false until disbursements is completed
    };
    beneficiary_information: {
        lender_name: string;
        lender_address: string; // "" = unknown / not yet available
        routing_number: string; // "" = unknown / not yet available
        account_number: string; // "" = unknown / not yet available
        verified: PayoffVerifiedStatus;
        insured: PayoffInsuredStatus;
    };
    documents: PayoffOrderDocument[];
}

export interface PayoffOrderFormRequest {
    file_id: File['id'];
    closing_date: string; // ISO date string
    borrower_id: Contact['borrower_id']; // selected primary borrower ID
    borrower_type: ContactType;
    loan_details: {
        lender_id: Lender['id'];
        loan_number: string;
        good_through_date: string; // ISO date string
    };
    seller_authorization_document?: {
        document_id: number; // ID from selected document or uploaded document
        s3_path: string; // S3 storage path for the document
        original_name: string; // Original filename uploaded by user
    };
}

export interface PayoffRequestResponse {
    request_id: number;
    status: 'submitted';
    message: string;
}

export interface BorrowerDisplayInfo extends Pick<
    Contact,
    'borrower_id' | 'borrower_type' | 'type' | 'first_name' | 'last_name' | 'full_name' | 'ssn_masked'
> {
    has_passed_idv: boolean; // Indicates if they've gone through IDV successfully
}

export interface BorrowerUpdateInfo extends BorrowerDisplayInfo {
    is_newly_created?: boolean;
    ssn?: string;
}

export interface PayoffOrderFormDisplayData {
    file_details: {
        id: File['id'];
        file_number: string;
        property_address: string;
        closing_date: null | string; // ISO 8601 date
    };
    borrowers: BorrowerDisplayInfo[];
    available_documents: PayoffOrderFormDisplayDocument[]; // all documents associated with the file
}

export interface PayoffOrderFormDisplayDocument {
    id: number;
    title: string;
    path: string;
    type?: PayoffOrderDocumentType;
}

export interface RequestNewLenderRequest {
    lender_name: string;
    user_id: User['id'];
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    notes?: string;
}

export interface RequestNewLenderResponse {
    status: 'success' | 'error';
    message: string;
}

export type SelectBorrowerErrors = 'unsavedBorrowerChanges' | 'selectedBorrower';

export type PendingContactOperation =
    | { type: 'attach'; payload: FileContactRequest }
    | { type: 'update'; borrowerId: Contact['borrower_id']; payload: FileContactRequest };

export type PendingClientUpdate = {
    clientId: Client['id'];
    payload: UpdateClientForFileRequest;
};
